import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import FullLogo from 'assets/images/logos/FullLogo.png';
import FitLogo from 'assets/images/logos/FitLogo.png';

import ROUTES from 'utils/routes';
import { backendFunction } from 'utils/backendApi';
import { useUser } from 'utils/userContext';
import {
    MONTHLY_SUBSCRIPTION,
    getAccessNote,
    getSubscriptionStatusLabel,
    getSubscriptionTimingNote,
    getPostCheckoutDestination,
    sanitizeInternalRedirectPath,
} from 'utils/subscription';

function Subscription() {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
    const redirectTo = React.useMemo(
        () => sanitizeInternalRedirectPath(searchParams.get('redirectTo')),
        [searchParams]
    );

    const {
        user,
        hasPaidAccess,
        isBillingExempt,
        subscriptionStatus,
    } = useUser();

    const [checkoutLoading, setCheckoutLoading] = React.useState(false);
    const [portalLoading, setPortalLoading] = React.useState(false);
    const [message, setMessage] = React.useState('');
    const accessNote = getAccessNote(user);
    const subscriptionTimingNote = getSubscriptionTimingNote(user);

    React.useEffect(() => {
        const checkoutStatus = searchParams.get('checkout');

        if (checkoutStatus !== 'success') return;

        const nextParams = new URLSearchParams();
        if (redirectTo) {
            nextParams.set('redirectTo', redirectTo);
        }
        const query = nextParams.toString();
        navigate(`${ROUTES.SUBSCRIPTION_SUCCESS}${query ? `?${query}` : ''}`, { replace: true });
    }, [navigate, redirectTo, searchParams]);

    const startCheckout = async () => {
        setCheckoutLoading(true);
        setMessage('');

        const { data, error } = await backendFunction('create-checkout-session', {
            method: 'POST',
            withAuth: true,
            body: {
                redirectTo,
            },
        });

        if (error || !data?.url) {
            setMessage(error || 'Unable to open checkout right now.');
            setCheckoutLoading(false);
            return;
        }

        window.location.assign(data.url);
    };

    const openBillingPortal = async () => {
        setPortalLoading(true);
        setMessage('');

        const { data, error } = await backendFunction('create-customer-portal-session', {
            method: 'POST',
            withAuth: true,
        });

        if (error || !data?.url) {
            setMessage(error || 'Unable to open billing settings right now.');
            setPortalLoading(false);
            return;
        }

        window.location.assign(data.url);
    };

    const showPortal = Boolean(user?.stripe_customer_id) && (hasPaidAccess || subscriptionStatus);

    return (
        <div
            className="min-h-screen flex flex-col items-center bg-background px-4 break-small:px-20 break-big:px-40"
            style={{ background: 'linear-gradient(to bottom, var(--background) 0%, var(--background) 18%, var(--primary) 700%)' }}
        >
            {(checkoutLoading || portalLoading) && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className='flex items-center justify-center flex-col'>
                        <div className="loader relative">
                            <div className="spinner w-28 h-28 rounded-full animate-spin border-t-[6px] border-b-[6px] border-white" />
                            <img src={FitLogo} alt="Loading..." className='absolute inset-0 m-auto p-4' />
                        </div>
                        <p className="text-center text-white p-4 font-bold">
                            Opening Stripe...
                        </p>
                    </div>
                </div>
            )}

            <nav className="w-full flex items-center justify-between py-5 z-10">
                <Link to={ROUTES.LANDING}><img src={FullLogo} alt="Kodeo Logo" className="h-6 w-auto" /></Link>
                <div className="flex gap-4 items-center">
                    <Link to={ROUTES.SETTINGS + '/subscription'} className="bg-background-light text-white font-bold px-6 py-2 rounded-2xl shadow-md hover:bg-background transition-all duration-500 text-lg border-2 border-primary">
                        Settings
                    </Link>
                </div>
            </nav>

            <div className="w-full max-w-[980px] flex flex-col gap-6 py-8 md:py-14">
                <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
                    <section className="rounded-[2rem] border-2 border-border bg-background-light/90 p-7 md:p-10 shadow-xl shadow-black/20">
                        <p className="text-primary font-bold uppercase tracking-[0.24em] text-sm">Membership</p>
                        <h1 className="text-white text-4xl md:text-5xl font-extrabold leading-tight mt-3">
                            Unlock the full Kodeo roadmap.
                        </h1>
                        <p className="text-text-light text-lg mt-4 max-w-[42rem]">
                            Create an account once, then use Stripe to turn on monthly access for every lesson and future curriculum update.
                        </p>

                        <div className="grid gap-3 sm:grid-cols-2 mt-8">
                            <div className="rounded-2xl border border-border p-4">
                                <h2 className="font-bold text-xl">What’s included</h2>
                                <p className="text-text-light mt-2">Every lesson, the full roadmap, progress tracking, and future content as it ships.</p>
                            </div>
                            <div className="rounded-2xl border border-border p-4">
                                <h2 className="font-bold text-xl">Built for families</h2>
                                <p className="text-text-light mt-2">Simple monthly billing, cancel anytime, and a guest preview stays free for first-time visitors.</p>
                            </div>
                        </div>
                    </section>

                    <aside className="rounded-[2rem] border-2 border-primary bg-[color:color-mix(in_srgb,var(--primary)_16%,var(--background))] p-7 md:p-8 shadow-xl shadow-primary/20">
                        <p className="text-text-light font-bold uppercase tracking-[0.24em] text-sm">One plan</p>
                        <h2 className="text-white text-3xl font-extrabold mt-3">{MONTHLY_SUBSCRIPTION.name}</h2>
                        <div className="text-white text-5xl font-black leading-none mt-5">{MONTHLY_SUBSCRIPTION.priceDisplay}</div>
                        <p className="text-text-light mt-3">{MONTHLY_SUBSCRIPTION.tagline}</p>

                        <div className="mt-6 rounded-2xl border border-border bg-background/60 p-4">
                            <div className="flex items-center justify-between gap-4">
                                <span className="text-text-light">Current status</span>
                                <span className="font-bold text-white">{getSubscriptionStatusLabel(user)}</span>
                            </div>
                            {accessNote && (
                                <p className="text-sm text-text-light mt-3">
                                    Access note: {accessNote}
                                </p>
                            )}
                            {subscriptionTimingNote && (
                                <p className="text-sm text-text-light mt-3">
                                    {subscriptionTimingNote}
                                </p>
                            )}
                        </div>

                        <div className="flex flex-col gap-3 mt-6">
                            {!hasPaidAccess && !isBillingExempt && (
                                <button
                                    onClick={startCheckout}
                                    disabled={checkoutLoading}
                                    className="w-full bg-primary text-background font-bold py-4 rounded-2xl shadow-sm shadow-primary text-xl border-2 border-primary disabled:opacity-60"
                                >
                                    Start monthly membership
                                </button>
                            )}

                            {hasPaidAccess && (
                                <button
                                    onClick={() => navigate(getPostCheckoutDestination(user, redirectTo, ROUTES.LESSONS))}
                                    className="w-full bg-primary text-background font-bold py-4 rounded-2xl shadow-sm shadow-primary text-xl border-2 border-primary"
                                >
                                    Continue learning
                                </button>
                            )}

                            {showPortal && (
                                <button
                                    onClick={openBillingPortal}
                                    disabled={portalLoading}
                                    className="w-full bg-transparent text-text font-bold py-4 rounded-2xl border-2 border-border hover:bg-background-pop transition-colors duration-300 disabled:opacity-60"
                                >
                                    Manage billing
                                </button>
                            )}

                            <Link
                                to={user?.isGuest ? ROUTES.LESSONS : ROUTES.LANDING}
                                className="w-full text-center bg-transparent text-text-light font-bold py-3 rounded-2xl border-2 border-border hover:bg-background-pop transition-colors duration-300"
                            >
                                {user?.isGuest ? 'Back to preview' : 'Back to home'}
                            </Link>
                        </div>

                        {message && <p className="text-sm text-text-light mt-4">{message}</p>}
                    </aside>
                </div>
            </div>
        </div>
    );
}

export default Subscription;
